import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import BlitzGrid from './BlitzGrid';
import {
  getToday, submitGuess, submitResult, VibeError, VibeToday, ResultResponse,
} from '../../services/vibecheckService';
import {
  buildShareText, DayState, getPlayerId, hasSeenIntro, loadDayState, loadStreak, markIntroSeen,
  MAX_TOTAL, Pin, recordPlay, ROUNDS, roundEmoji, saveDayState, saveStreak, Streak, verdict,
} from '../../utils/vibecheck';
import { config } from '../../config/environment';

const SHARE_URL = `${config.app.baseUri}/vibecheck`;

const useCountdown = (iso?: string) => {
  const [left, setLeft] = useState('');
  useEffect(() => {
    if (!iso) return;
    const tick = () => {
      const ms = Math.max(0, Date.parse(iso) - Date.now());
      const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000), s = Math.floor((ms % 60000) / 1000);
      setLeft(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [iso]);
  return left;
};

/** Plays a fixed window of a preview clip. */
const useClip = (url: string | undefined, start: number, seconds: number) => {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(false);

  const stop = useCallback(() => {
    audio.current?.pause();
    setPlaying(false);
  }, []);

  useEffect(() => {
    setProgress(0); setPlaying(false); setError(false);
    if (!url) return;
    const a = new Audio(url);
    a.preload = 'auto';
    audio.current = a;
    a.onerror = () => { setError(true); setPlaying(false); };
    a.ontimeupdate = () => {
      const p = (a.currentTime - start) / seconds;
      setProgress(Math.min(1, Math.max(0, p)));
      if (p >= 1) { a.pause(); setPlaying(false); }
    };
    a.onended = () => setPlaying(false);
    return () => { a.pause(); a.onerror = null; a.ontimeupdate = null; a.onended = null; audio.current = null; };
  }, [url, start, seconds]);

  const play = useCallback(() => {
    const a = audio.current;
    if (!a) return;
    setError(false);
    try { a.currentTime = start; } catch { /* metadata not loaded yet; start from 0 */ }
    setProgress(0);
    a.play().then(() => setPlaying(true)).catch(() => { setError(true); setPlaying(false); });
  }, [start]);

  return { playing, progress, error, play, stop };
};

const VibeCheck: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const muted = theme.palette.text.secondary;
  const card = {
    background: theme.palette.background.paper,
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
    borderRadius: '16px',
  };
  const pill: React.CSSProperties = {
    padding: '12px 24px', borderRadius: 100, border: 'none', fontWeight: 600, fontSize: 14, cursor: 'pointer',
    background: isDark ? '#fff' : '#000', color: isDark ? '#000' : '#fff', fontFamily: 'inherit',
  };

  const [today, setToday] = useState<VibeToday | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [state, setState] = useState<DayState | null>(null);
  const [pin, setPin] = useState<Pin | null>(null);
  const [result, setResult] = useState<ResultResponse | null>(null);
  const [resultError, setResultError] = useState(false);
  const [streak, setStreak] = useState<Streak>(loadStreak);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showHelp, setShowHelp] = useState(() => !hasSeenIntro());
  const submitting = useRef(false);

  const countdown = useCountdown(today?.nextResetAt);

  // This page is unlisted while it's being workshopped
  useEffect(() => {
    const m = document.createElement('meta');
    m.name = 'robots'; m.content = 'noindex';
    document.head.appendChild(m);
    return () => { document.head.removeChild(m); };
  }, []);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const t = await getToday();
      setToday(t);
      setState(loadDayState(t.date));
    } catch (e) {
      setLoadError(e instanceof VibeError ? e.message : "Something went wrong loading today's round.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const done = !!state && state.reveals.length >= ROUNDS;
  const round = state ? Math.min(state.reveals.length, ROUNDS - 1) : 0;
  // A round is "revealed" once its guess is saved but the player hasn't advanced
  const [revealing, setRevealing] = useState<number | null>(null);
  const current = state && revealing !== null ? state.reveals[revealing] : null;

  const clip = useClip(today?.clips[revealing ?? round], today?.clipStart ?? 10, today?.clipSeconds ?? 10);

  const closeHelp = () => { markIntroSeen(); setShowHelp(false); };

  const lockIn = async () => {
    if (!pin || !state || busy || revealing !== null) return;
    setBusy(true); setMessage(null);
    try {
      const reveal = await submitGuess(round, pin);
      const next: DayState = { ...state, pins: [...state.pins, pin], reveals: [...state.reveals, reveal] };
      setState(next); saveDayState(next);
      setRevealing(next.reveals.length - 1);
    } catch (e) {
      setMessage(e instanceof VibeError ? e.message : 'Something went wrong. Try again.');
    } finally { setBusy(false); }
  };

  const nextRound = () => { clip.stop(); setRevealing(null); setPin(null); };

  // After the last reveal is dismissed, submit to the leaderboard once
  const finish = useCallback(async (s: DayState) => {
    if (submitting.current) return;
    submitting.current = true; setResultError(false);
    try {
      setResult(await submitResult(getPlayerId(), s.pins));
      setStreak(prev => { const n = recordPlay(prev, s.date); saveStreak(n); return n; });
    } catch { setResultError(true); }
    finally { submitting.current = false; }
  }, []);

  useEffect(() => {
    if (done && revealing === null && state && !result) finish(state);
  }, [done, revealing, state, result, finish]);

  const totals = state?.reveals.map(r => r.score) ?? [];
  const total = totals.reduce((a, b) => a + b, 0);
  const shareText = today ? buildShareText({ number: today.number, scores: totals, betterThan: result?.betterThan ?? null, url: SHARE_URL }) : '';

  const share = async () => {
    try {
      if (navigator.share && /Mobi|Android/i.test(navigator.userAgent)) { await navigator.share({ text: shareText }); return; }
    } catch { /* cancelled; fall through to copy */ }
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch { setMessage('Couldn’t copy. Select the text above instead.'); }
  };

  const wrap: React.CSSProperties = { maxWidth: 520, margin: '0 auto', padding: '8px 16px 64px' };

  if (loadError) {
    return (
      <div style={wrap}>
        <Box sx={{ ...card, p: 3, textAlign: 'center', mt: 4 }}>
          <Typography sx={{ mb: 2 }}>{loadError}</Typography>
          <button style={pill} onClick={load}>Try again</button>
        </Box>
      </div>
    );
  }
  if (!today || !state) return <div style={wrap}><Typography sx={{ textAlign: 'center', mt: 8, color: muted }}>Loading today’s round…</Typography></div>;

  const header = (
    <Box sx={{ textAlign: 'center', mb: 2.5, mt: 2 }}>
      <Typography component="h1" sx={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em' }}>Vibecheck <span style={{ fontWeight: 500, color: muted }}>Blitz</span></Typography>
      <Typography sx={{ fontSize: 13, color: muted }}>
        #{today.number} · {done && revealing === null ? 'Done for today' : `Song ${round + 1} of ${ROUNDS}`}
        {' · '}
        <button onClick={() => setShowHelp(true)} style={{ all: 'unset', cursor: 'pointer', textDecoration: 'underline' }}>How to play</button>
      </Typography>
    </Box>
  );

  const help = showHelp && (
    <Box sx={{ ...card, p: 2.5, mb: 2.5 }}>
      <Typography sx={{ fontWeight: 700, mb: 1 }}>How to play</Typography>
      <Box component="ol" sx={{ m: 0, pl: 2.5, fontSize: 14, lineHeight: 1.7 }}>
        <li>Press play and listen to a 10-second clip. No names, just the feel.</li>
        <li>Drop a pin on the grid: left is sad, right is happy; bottom is calm, top is intense.</li>
        <li>Lock it in to see where the song really sits. Closer pins score more (up to 100).</li>
        <li>Five songs a day, {MAX_TOTAL} points max. Same songs for everyone.</li>
      </Box>
      <Box sx={{ mt: 2 }}><button style={pill} onClick={closeHelp}>Got it</button></Box>
    </Box>
  );

  const scoreStrip = (
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }} aria-label="Round scores">
      {Array.from({ length: ROUNDS }, (_, i) => {
        const r = state.reveals[i];
        const active = i === round && !done;
        return (
          <Box key={i} sx={{
            width: 44, height: 36, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
            border: `1.5px solid ${active ? theme.palette.primary.main : (isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)')}`,
            color: r ? theme.palette.text.primary : muted,
          }}>
            {r ? `${roundEmoji(r.score)}${''}` : i + 1}
          </Box>
        );
      })}
    </Box>
  );

  const player = (
    <Box sx={{ ...card, p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
      <button
        onClick={clip.playing ? clip.stop : clip.play}
        aria-label={clip.playing ? 'Pause clip' : 'Play clip'}
        style={{ ...pill, width: 56, height: 56, borderRadius: 28, padding: 0, fontSize: 20, flexShrink: 0 }}
      >
        {clip.playing ? '❚❚' : '▶'}
      </button>
      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
          {clip.error ? 'Couldn’t play this clip' : clip.playing ? 'Listening…' : clip.progress > 0 ? 'Replay the clip' : 'Play the 10-second clip'}
        </Typography>
        <Box sx={{ height: 6, borderRadius: 3, mt: 1, background: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          <Box sx={{ height: '100%', width: `${clip.progress * 100}%`, background: theme.palette.primary.main, transition: 'width 0.25s linear' }} />
        </Box>
        {clip.error && <Typography sx={{ fontSize: 12, color: muted, mt: 0.5 }}>Check your volume or connection, then press play again.</Typography>}
      </Box>
    </Box>
  );

  const revealCard = current && (
    <Box sx={{ ...card, p: 2, mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
      {current.target.image && <img src={current.target.image} alt="" width={56} height={56} style={{ borderRadius: 8, flexShrink: 0 }} />}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{current.target.name}</Typography>
        <Typography sx={{ fontSize: 13, color: muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{current.target.artist}</Typography>
        <a href={`https://open.spotify.com/track/${current.target.id}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: theme.palette.primary.main }}>Open in Spotify</a>
      </Box>
      <Box sx={{ textAlign: 'center' }}>
        <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1 }}>{current.score}</Typography>
        <Typography sx={{ fontSize: 11, color: muted }}>/ 100</Typography>
      </Box>
    </Box>
  );

  // ---------- final screen ----------
  if (done && revealing === null) {
    const hist = result?.stats.histogram ?? [];
    const maxH = Math.max(1, ...hist);
    const myBucket = result ? Math.floor(result.total / result.bucketSize) : -1;
    return (
      <div style={wrap}>
        {header}
        {help}
        <Box sx={{ ...card, p: 3, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 13, color: muted }}>{verdict(total)}</Typography>
          <Typography sx={{ fontSize: 56, fontWeight: 800, lineHeight: 1.1, letterSpacing: '-0.03em' }}>{total}<span style={{ fontSize: 22, color: muted, fontWeight: 600 }}> / {MAX_TOTAL}</span></Typography>
          <Typography sx={{ fontSize: 28, letterSpacing: 4, my: 1 }} aria-label="Round results">{totals.map(roundEmoji).join('')}</Typography>
          {result?.betterThan != null && <Typography sx={{ fontWeight: 600 }}>Better than {result.betterThan}% of players</Typography>}
          {resultError && (
            <Typography sx={{ fontSize: 13, color: muted, mt: 1 }}>
              Couldn’t reach the leaderboard. <button onClick={() => state && finish(state)} style={{ all: 'unset', cursor: 'pointer', textDecoration: 'underline' }}>Retry</button>
            </Typography>
          )}

          <Box component="pre" sx={{ fontFamily: 'inherit', fontSize: 14, background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)', borderRadius: '12px', p: 1.5, my: 2, whiteSpace: 'pre-wrap', textAlign: 'center' }}>{shareText}</Box>
          <button style={pill} onClick={share}>{copied ? 'Copied!' : 'Share result'}</button>
          {message && <Typography sx={{ fontSize: 13, color: muted, mt: 1 }}>{message}</Typography>}
        </Box>

        <Box sx={{ ...card, p: 2.5, mt: 2 }}>
          {state.reveals.map((r, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderTop: i ? `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}` : 'none' }}>
              {r.target.image && <img src={r.target.image} alt="" width={40} height={40} style={{ borderRadius: 6 }} />}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.target.name}</Typography>
                <Typography sx={{ fontSize: 12, color: muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.target.artist}</Typography>
              </Box>
              <Typography sx={{ fontWeight: 700 }}>{roundEmoji(r.score)} {r.score}</Typography>
            </Box>
          ))}
        </Box>

        {result && result.stats.total >= 1 && (
          <Box sx={{ ...card, p: 2.5, mt: 2 }}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>Today’s scores · {result.stats.total} player{result.stats.total === 1 ? '' : 's'}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: 70 }} aria-hidden>
              {hist.map((n, i) => (
                <Box key={i} sx={{ flex: 1, height: `${Math.max(3, (n / maxH) * 100)}%`, borderRadius: '2px', background: i === myBucket ? theme.palette.primary.main : (isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)') }} />
              ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: muted, mt: 0.5 }}><span>0</span><span>{MAX_TOTAL}</span></Box>
          </Box>
        )}

        <Box sx={{ ...card, p: 2.5, mt: 2, display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
          <div><Typography sx={{ fontSize: 22, fontWeight: 800 }}>{streak.current}</Typography><Typography sx={{ fontSize: 12, color: muted }}>day streak</Typography></div>
          <div><Typography sx={{ fontSize: 22, fontWeight: 800 }}>{streak.best}</Typography><Typography sx={{ fontSize: 12, color: muted }}>best</Typography></div>
          <div><Typography sx={{ fontSize: 22, fontWeight: 800 }}>{streak.played}</Typography><Typography sx={{ fontSize: 12, color: muted }}>played</Typography></div>
          <div><Typography sx={{ fontSize: 22, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{countdown}</Typography><Typography sx={{ fontSize: 12, color: muted }}>next round</Typography></div>
        </Box>
      </div>
    );
  }

  // ---------- play screen ----------
  const isLast = revealing === ROUNDS - 1;
  return (
    <div style={wrap}>
      {header}
      {help}
      {scoreStrip}
      {player}
      <BlitzGrid
        pin={current ? state.pins[revealing as number] : pin}
        onPlace={current ? undefined : setPin}
        target={current ? current.target : null}
      />
      {revealCard}
      <Box sx={{ textAlign: 'center', mt: 2.5 }}>
        {current ? (
          <button style={pill} onClick={() => { if (isLast) { clip.stop(); setRevealing(null); } else nextRound(); }}>
            {isLast ? 'See my results' : 'Next song'}
          </button>
        ) : (
          <button style={{ ...pill, opacity: pin && !busy ? 1 : 0.4, cursor: pin && !busy ? 'pointer' : 'not-allowed' }} disabled={!pin || busy} onClick={lockIn}>
            {busy ? 'Checking…' : 'Lock it in'}
          </button>
        )}
        {message && <Typography role="alert" sx={{ fontSize: 13, color: theme.palette.error.main, mt: 1.5 }}>{message}</Typography>}
      </Box>
    </div>
  );
};

export default VibeCheck;
